import { useState, useEffect, useRef, useCallback } from 'react'

// TypeScript types for Web Speech API
interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent {
  error: string
  message: string
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition
    webkitSpeechRecognition: new () => SpeechRecognition
  }
}

export type ListeningState = 'idle' | 'listening' | 'processing' | 'wake_word_detected'

export interface UseSpeechRecognitionOptions {
  alwaysListening?: boolean
  wakeWord?: string
  restartDelay?: number // ms to wait before restarting in always-on mode (battery-friendly)
  onWakeWordDetected?: () => void
  onTranscriptReady?: (transcript: string) => void
}

export interface UseSpeechRecognitionReturn {
  isListening: boolean
  transcript: string
  interimTranscript: string
  startListening: () => void
  stopListening: () => void
  resetTranscript: () => void
  isSupported: boolean
  error: string | null
  listeningState: ListeningState
  alwaysListening: boolean
  setAlwaysListening: (enabled: boolean) => void
  wakeWordDetected: boolean
}

export function useSpeechRecognition(options: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionReturn {
  const {
    wakeWord = 'hey coach',
    restartDelay = 1000,
    onWakeWordDetected,
    onTranscriptReady
  } = options

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [listeningState, setListeningState] = useState<ListeningState>('idle')
  const [alwaysListening, setAlwaysListening] = useState(options.alwaysListening || false)
  const [wakeWordDetected, setWakeWordDetected] = useState(false)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const alwaysListeningRef = useRef(alwaysListening)

  // Check browser support
  const isSupported = typeof window !== 'undefined' &&
    (!!window.SpeechRecognition || !!window.webkitSpeechRecognition)

  // Keep alwaysListeningRef in sync
  useEffect(() => {
    alwaysListeningRef.current = alwaysListening
  }, [alwaysListening])

  // Helper to check for wake word in transcript
  const checkForWakeWord = useCallback((text: string): boolean => {
    if (!wakeWord) return true // If no wake word configured, always process
    const normalizedText = text.toLowerCase().trim()
    const normalizedWakeWord = wakeWord.toLowerCase().trim()
    return normalizedText.includes(normalizedWakeWord)
  }, [wakeWord])

  // Auto-restart logic for always-on mode
  const scheduleRestart = useCallback(() => {
    if (!alwaysListeningRef.current || !recognitionRef.current) return

    // Clear any existing restart timeout
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current)
    }

    // Schedule restart after delay (battery-friendly)
    restartTimeoutRef.current = setTimeout(() => {
      if (alwaysListeningRef.current && recognitionRef.current) {
        try {
          setListeningState('listening')
          recognitionRef.current.start()
        } catch (err) {
          console.warn('Auto-restart failed:', err)
          // Try again after another delay
          scheduleRestart()
        }
      }
    }, restartDelay)
  }, [restartDelay])

  // Initialize recognition
  useEffect(() => {
    if (!isSupported) return

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognitionClass()

    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setIsListening(true)
      setError(null)
      setListeningState('listening')
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = ''
      let interimText = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimText += result[0].transcript
        }
      }

      // Check for wake word in interim results for faster response
      if (interimText && checkForWakeWord(interimText)) {
        setWakeWordDetected(true)
        setListeningState('wake_word_detected')
        onWakeWordDetected?.()
      }

      if (finalTranscript) {
        const hasWakeWord = checkForWakeWord(finalTranscript)

        if (hasWakeWord) {
          setWakeWordDetected(true)
          setListeningState('processing')

          // Extract command after wake word
          const wakeWordIndex = finalTranscript.toLowerCase().indexOf(wakeWord.toLowerCase())
          const command = wakeWordIndex !== -1
            ? finalTranscript.substring(wakeWordIndex + wakeWord.length).trim()
            : finalTranscript

          setTranscript(prev => prev + command)
          onTranscriptReady?.(command)
        } else if (!wakeWord) {
          // No wake word required, process everything
          setTranscript(prev => prev + finalTranscript)
          onTranscriptReady?.(finalTranscript)
        }
      }
      setInterimTranscript(interimText)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error)

      // Handle mobile browser limitations gracefully
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      const errorMsg = isMobile && event.error === 'not-allowed'
        ? 'Microphone access requires user interaction on mobile browsers. Tap the microphone button to start.'
        : getErrorMessage(event.error)

      setError(errorMsg)
      setIsListening(false)
      setListeningState('idle')

      // Don't auto-restart on permission errors or aborted
      if (event.error !== 'not-allowed' && event.error !== 'aborted' && alwaysListeningRef.current) {
        scheduleRestart()
      }
    }

    recognition.onend = () => {
      setIsListening(false)
      setInterimTranscript('')

      // Auto-restart if in always-on mode
      if (alwaysListeningRef.current) {
        setListeningState('idle')
        scheduleRestart()
      } else {
        setListeningState('idle')
        setWakeWordDetected(false)
      }
    }

    recognitionRef.current = recognition

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current)
      }
      recognition.abort()
    }
  }, [isSupported, checkForWakeWord, wakeWord, onWakeWordDetected, onTranscriptReady, scheduleRestart])

  const startListening = useCallback(() => {
    if (!recognitionRef.current || !isSupported) {
      setError('Speech recognition not supported in this browser')
      return
    }

    setTranscript('')
    setInterimTranscript('')
    setError(null)
    setWakeWordDetected(false)

    try {
      setListeningState('listening')
      recognitionRef.current.start()
    } catch (err) {
      // Recognition might already be running
      console.warn('Speech recognition start error:', err)
    }
  }, [isSupported])

  const stopListening = useCallback(() => {
    // Clear any pending restart
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current)
      restartTimeoutRef.current = null
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }

    setListeningState('idle')
    setWakeWordDetected(false)
  }, [])

  const resetTranscript = useCallback(() => {
    setTranscript('')
    setInterimTranscript('')
    setWakeWordDetected(false)
  }, [])

  // Effect to handle always-on mode changes
  useEffect(() => {
    if (alwaysListening && !isListening && isSupported) {
      // Start listening when always-on mode is enabled
      startListening()
    } else if (!alwaysListening && isListening) {
      // Stop listening when always-on mode is disabled
      stopListening()
    }
  }, [alwaysListening, isListening, isSupported, startListening, stopListening])

  return {
    isListening,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript,
    isSupported,
    error,
    listeningState,
    alwaysListening,
    setAlwaysListening,
    wakeWordDetected
  }
}

// Helper function to convert error codes to user-friendly messages
function getErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'no-speech':
      return 'No speech was detected. Please try again.'
    case 'audio-capture':
      return 'No microphone was found. Please check your microphone.'
    case 'not-allowed':
      return 'Microphone permission was denied. Please allow microphone access.'
    case 'network':
      return 'Network error occurred. Please check your connection.'
    case 'aborted':
      return 'Speech recognition was aborted.'
    case 'language-not-supported':
      return 'Language not supported.'
    case 'service-not-allowed':
      return 'Speech recognition service not allowed.'
    default:
      return `Speech recognition error: ${errorCode}`
  }
}
